import { useGoToTarget } from "@/features/auth/hooks/useGoToTarget";
import { AppRoutes } from "@/routes/config";
import { createUrlWithParams, getSafeRedirectURL } from "@/utils/url";
import { useNavigate } from "react-router-dom";

type TPostLoginTarget = {
  /** OAuth (SSO) product code. When present, the session is exchanged for a product token first. */
  productCode?: string | null;
  /** User-supplied redirect target. Always validated before use. */
  redirectUrl?: string | null;
};

type TPostLoginRedirectOptions = {
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
};

/**
 * Resolves where to send the user once authentication succeeds. OAuth (SSO) logins route through
 * the mint-redirect page to exchange the session for a product token; standard logins go to the
 * safe redirect target (in-app via navigate, external via location).
 *
 * Shared by every post-auth surface: the passkey flow, the login-verify form, and the Google
 * (Cognito) callback.
 */
export const usePostLoginRedirect = () => {
  const navigate = useNavigate();
  const goToTarget = useGoToTarget();

  return ({ productCode, redirectUrl }: TPostLoginTarget, options?: TPostLoginRedirectOptions) => {
    const targetUrl = getSafeRedirectURL(redirectUrl);

    if (productCode) {
      navigate(createUrlWithParams(AppRoutes.mintOauthRedirect, { productCode, redirectUrl: targetUrl }), options);
      return;
    }

    goToTarget(targetUrl, options);
  };
};
